import { useState } from 'react'
import { supabase } from './lib/supabase'

// SHA-256 simple via Web Crypto API
async function sha256(message) {
  const msgBuffer = new TextEncoder().encode(message)
  const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

export default function Auth({ onLogin }) {
  const [pin, setPin] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    if (pin.length < 4) return
    setLoading(true)
    setError('')

    try {
      const pinHash = await sha256(pin)
      const { data, error: dbError } = await supabase
        .from('config')
        .select('cle, valeur')
        .in('cle', ['pin_benevole', 'pin_admin'])

      if (dbError || !data) {
        setError('Erreur de connexion a la base')
        setLoading(false)
        return
      }

      const pinBenevole = data.find(d => d.cle === 'pin_benevole')?.valeur
      const pinAdmin = data.find(d => d.cle === 'pin_admin')?.valeur

      if (pinHash === pinAdmin) {
        sessionStorage.setItem('role', 'admin')
        onLogin('admin')
      } else if (pinHash === pinBenevole) {
        sessionStorage.setItem('role', 'benevole')
        onLogin('benevole')
      } else {
        setError('PIN incorrect')
        setPin('')
      }
    } catch(e) {
      setError('Erreur inattendue')
    }
    setLoading(false)
  }

  function handlePinInput(val) {
    if (val.length <= 6 && /^\d*$/.test(val)) setPin(val)
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a7a5e 0%, #0d4d3a 100%)'
    }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: '40px 32px', width: 320,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'center'
      }}>
        <div style={{fontSize: 48, marginBottom: 8}}>
          {String.fromCodePoint(0x1F6B5)}
        </div>
        <h1 style={{fontSize: 22, color: '#1a7a5e', marginBottom: 4}}>Course Event</h1>
        <p style={{fontSize: 13, color: '#888', marginBottom: 28}}>Entrez votre PIN pour acceder</p>

        <form onSubmit={handleSubmit}>
          <div style={{display: 'flex', justifyContent: 'center', gap: 10, marginBottom: 24}}>
            {[0,1,2,3,4,5].map(i => (
              <div key={i} style={{
                width: 36, height: 48, borderRadius: 8,
                border: '2px solid ' + (i < pin.length ? '#1a7a5e' : '#ddd'),
                background: i < pin.length ? '#e8f5f0' : '#f9f9f9',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 20, color: '#1a7a5e', fontWeight: 700,
                transition: 'all 0.15s'
              }}>
                {i < pin.length ? String.fromCodePoint(0x25CF) : ''}
              </div>
            ))}
          </div>

          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={e => handlePinInput(e.target.value)}
            placeholder="PIN"
            autoFocus
            style={{
              position: 'absolute', opacity: 0, pointerEvents: 'none'
            }}
          />

          {/* Pavé numérique */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16}}>
            {[1,2,3,4,5,6,7,8,9,'',0,'⌫'].map((num, i) => (
              <button
                key={i}
                type={num === 0 || typeof num === 'number' ? 'button' : 'button'}
                onClick={() => {
                  if (num === '⌫') setPin(p => p.slice(0,-1))
                  else if (num !== '') handlePinInput(pin + num)
                }}
                style={{
                  height: 52, borderRadius: 10, border: '1px solid #eee',
                  background: num === '⌫' ? '#fee' : num === '' ? 'transparent' : 'white',
                  fontSize: num === '⌫' ? 20 : 22, fontWeight: 600, color: '#333',
                  cursor: num === '' ? 'default' : 'pointer',
                  boxShadow: num !== '' && num !== '⌫' ? '0 2px 4px rgba(0,0,0,0.06)' : 'none',
                  transition: 'all 0.1s'
                }}
                disabled={num === ''}
              >
                {num === '' ? '' : num}
              </button>
            ))}
          </div>

          {error && (
            <div style={{color: '#e74c3c', fontSize: 13, marginBottom: 12, fontWeight: 600}}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={pin.length < 4 || loading}
            style={{
              width: '100%', height: 48, borderRadius: 10, border: 'none',
              background: pin.length >= 4 ? '#1a7a5e' : '#ccc',
              color: 'white', fontSize: 16, fontWeight: 700, cursor: pin.length >= 4 ? 'pointer' : 'default',
              transition: 'background 0.2s'
            }}
          >
            {loading ? 'Verification...' : 'Connexion'}
          </button>
        </form>
      </div>
    </div>
  )
}
